import { useRouter } from 'next/router';
import { useActiveConversation } from './ActiveConversationContext';
import { queryPath } from '../../lib/query-path';
import { PACK_HANDOFF_PARAM } from '../../lib/pack-handoff';

/**
 * Start a fresh thread in place: clear the active conversation and strip any
 * conversation/handoff params off the CURRENT URL — no navigation, and no size
 * change (the chat keeps whatever size it's at; the caller decides whether to
 * also maximize). The first send then stamps the new id back onto the URL.
 *
 * Returns the URL-strip promise. A caller that ALSO maximizes (so a fresh
 * ChatSurface mounts) must `await` this first: the strip is async, and a chat
 * mounting before it lands would re-seed the just-cleared conversation from the
 * still-stale `?conversation=<id>` in the URL.
 */
export function useStartNewConversation(): () => Promise<void> {
  const router = useRouter();
  const { setActiveConversationId, markFreshChat } = useActiveConversation();
  return async () => {
    markFreshChat();
    setActiveConversationId(null);
    const query: Record<string, string | string[] | undefined> = { ...router.query };
    delete query.conversation;
    delete query.message;
    delete query.new;
    delete query.prefill;
    delete query.autosend;
    delete query.outreach;
    delete query[PACK_HANDOFF_PARAM];
    if (router.pathname === '/chats') query.new = '1';
    // Display path is the current screen without its query — the removed params
    // were the only user-facing ones.
    const display = typeof window !== 'undefined' ? window.location.pathname : '';
    await router.replace({ pathname: router.pathname, query }, queryPath(display, query), { shallow: true });
  };
}
